import { makeStyles, Theme, Grid, Paper } from '@material-ui/core';

import {
  CatalogSearchResultListItem,
  type CatalogSearchResultListItemProps,
} from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';

import { SearchType } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchFilter,
  SearchResult,
  SearchPagination,
  useSearch,
} from '@backstage/plugin-search-react';
import {
  CatalogIcon,
  Content,
  DocsIcon,
  Header,
  Page,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useAdminGroupMember } from '../devtools/AdminGroupMemberContext';
import { isCatalogEntityPageAllowedForNonAdmin } from '../../constants/catalogKinds';

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  filter: {
    '& + &': {
      marginTop: theme.spacing(2.5),
    },
  },
}));

/** Fields the catalog collator adds; not declared on IndexableDocument in search-common yet. */
type CatalogSearchDocument = NonNullable<
  CatalogSearchResultListItemProps['result']
> & {
  kind?: string;
};

function GatedCatalogSearchResultListItem(props: CatalogSearchResultListItemProps) {
  const admin = useAdminGroupMember();
  const kind = (
    (props.result as CatalogSearchDocument | undefined)?.kind ?? ''
  ).toLowerCase();
  if (admin !== true && kind && !isCatalogEntityPageAllowedForNonAdmin(kind)) {
    return null;
  }
  return <CatalogSearchResultListItem icon={<CatalogIcon />} {...props} />;
}

const SearchPage = () => {
  const classes = useStyles();
  const { types } = useSearch();
  const catalogApi = useApi(catalogApiRef);
  const admin = useAdminGroupMember();

  return (
    <Page themeId="home">
      <Header title="Search" />
      <Content>
        <Grid container direction="row">
          <Grid item xs={12}>
            <Paper className={classes.bar}>
              <SearchBar />
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <SearchType.Accordion
              name="Result Type"
              defaultValue="software-catalog"
              types={[
                {
                  value: 'software-catalog',
                  name: 'Software Catalog',
                  icon: <CatalogIcon />,
                },
                {
                  value: 'techdocs',
                  name: 'Documentation',
                  icon: <DocsIcon />,
                },
              ]}
            />
            <Paper className={classes.filters}>
              {types.includes('techdocs') && (
                <SearchFilter.Select
                  className={classes.filter}
                  label="Entity"
                  name="name"
                  values={async () => {
                    const { items } = await catalogApi.getEntities({
                      fields: ['metadata.name'],
                      filter: {
                        'metadata.annotations.backstage.io/techdocs-ref':
                          CATALOG_FILTER_EXISTS,
                      },
                    });

                    const names = items.map(entity => entity.metadata.name);
                    names.sort();
                    return names;
                  }}
                />
              )}
              <SearchFilter.Select
                className={classes.filter}
                label="Kind"
                name="kind"
                values={async () => {
                  if (admin === true) {
                    const { facets } = await catalogApi.getEntityFacets({
                      facets: ['kind'],
                    });
                    const kinds = (facets.kind ?? []).map(f => f.value);
                    kinds.sort((a, b) => a.localeCompare(b));
                    return kinds.length ? kinds : ['Component', 'Template'];
                  }
                  return ['Component', 'Template'];
                }}
              />
              <SearchFilter.Checkbox
                className={classes.filter}
                label="Lifecycle"
                name="lifecycle"
                values={['experimental', 'production']}
              />
            </Paper>
          </Grid>
          <Grid item xs={9}>
            <SearchPagination />
            <SearchResult>
              <GatedCatalogSearchResultListItem />
              <TechDocsSearchResultListItem icon={<DocsIcon />} />
            </SearchResult>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export const searchPage = <SearchPage />;
